/* eslint-disable no-bitwise */
import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { SuspendRMReasons } from '@ps/platform-message-framework/maindbm/SuspendRMReasons';
import { UserFlags } from '@ps/platform-message-framework/maindbm/UserFlags';
import { UserFlags2 } from '@ps/platform-message-framework/maindbm/UserFlags2';
import { UserFlags3 } from '@ps/platform-message-framework/maindbm/UserFlags3';
import { UserPrivileges } from '@ps/platform-message-framework/maindbm/UserPrivileges';
import { UserPrivileges2 } from '@ps/platform-message-framework/maindbm/UserPrivileges2';
import { UserPrivileges3 } from '@ps/platform-message-framework/maindbm/UserPrivileges3';
import { RegulatorAccValEventType } from '@ps/platform-message-framework/regulator/RegulatorAccValEventType';
import { RegulatorBanPopupType } from '@ps/platform-message-framework/regulator/RegulatorBanPopupType';
import { RegulatorPlayerValPopupType } from '@ps/platform-message-framework/regulator/RegulatorPlayerValPopupType';
import { Country, Currency, EmailFlag, FeatureFlag, License } from '@ps/web-core-enums';
import { get } from 'lodash';
import type { Selector } from 'reselect';

import { depositLimitExtendedSites, select } from '../../../index';
import type { RootState, SelectorCreator, Slicer } from '../../types';
import type {
  AccountInfoLastLoginDetails,
  AccountInfoWinLossData,
  AccountSummary,
  EmailValidationFlags,
  Flag,
  LoginLimitInfo
} from '../../types/Account';
import { LoginSource } from '../session/session';

import type { AccountState } from './account';

const selectors = (slice: Slicer<AccountState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getUserId() {
    return slice((state) => state.userId);
  },
  getGamingAccountNumber() {
    return slice((state) => state.gamingAccountNumber);
  },
  getCity() {
    return slice((state) => state.city);
  },
  getAddress() {
    return slice((state) => state.addr1);
  },
  getAccValEventType() {
    return slice((state) => state.accValEventType);
  },
  getBuildingNumber() {
    return slice((state) => state.buildingNumber);
  },
  getStreetName() {
    return slice((state) => state.streetName);
  },
  getDateOfBirth() {
    return slice((state) => (state.dateOfBirth ? new Date(state.dateOfBirth) : undefined));
  },
  getNationalDocumentNumber() {
    return slice((state) => state.nationalDocumentNumber);
  },
  getZipCode() {
    return slice((state) => state.zipCode);
  },
  getFirstName() {
    return slice((state) => state.firstName);
  },
  getLastName() {
    return slice((state) => state.lastName);
  },
  getFullName() {
    return slice((state) => state.fullName);
  },
  getGender() {
    return slice((state) => state.gender);
  },
  getPlaceOfBirth() {
    return slice((state) => state.placeOfBirth);
  },
  getOrientationNumber() {
    return slice((state) => state.orientationNumber);
  },
  getDistrict() {
    return slice((state) => state.district);
  },
  getPartOfMunicipality() {
    return slice((state) => state.partOfMunicipality);
  },
  getRegistrationDate() {
    return slice((state) => (state.registered ? new Date(state.registered) : undefined));
  },
  // TODO: Add getFullPlaceOfBirth when we have states.json and countries.json
  getEulaAcceptanceDate() {
    return slice((state) => (state.eulaSigned ? new Date(state.eulaSigned) : undefined));
  },
  getEmail() {
    return slice((state) => state.email);
  },
  getAddressState() {
    return slice((state) => state.state);
  },
  // TODO: Add getFullAddress when we have states.json and countries.json
  getAccountSummary() {
    return slice((state) => state.accountSummary as Partial<AccountSummary>);
  },
  getAmlEventType() {
    return slice((state) => state.amlEventType);
  },
  getAmlDaysLeft() {
    return slice((state) => state.amlDaysLeft);
  },
  getAmlDepositThresholdReachedTimestamp() {
    return slice((state) => state.amlDepositThresholdReachedTimestamp);
  },
  getBalances() {
    return slice((state) => state.balances);
  },
  getTotalBalance() {
    return slice((state) => state.totalBalance);
  },
  getTotalBalanceInUsd() {
    return slice((state) => state.totalBalanceInUsd);
  },
  getPlayChips() {
    return slice((state) => state.playChips);
  },
  getLastLoginDetails() {
    return slice((state) => get(state, 'info.lastLoginDetails', {}) as AccountInfoLastLoginDetails);
  },
  getSessionWinLossData() {
    return slice((state) => get(state, 'info.sessionWinLossData', {}) as AccountInfoWinLossData);
  },
  getLoginLimitInfo() {
    return slice((state) => get(state, 'info.loginLimitInfo', {}) as Partial<LoginLimitInfo>);
  },
  getCommunicationPreferences() {
    return slice((state) => {
      const lo = state.privileges3?.lo as number;
      return {
        mailListPoker: (lo & UserPrivileges3.NO_MAIL_LIST_POKER) === 0,
        mailListCasino: (lo & UserPrivileges3.NO_MAIL_LIST_CASINO) === 0,
        mailListSports: (lo & UserPrivileges3.NO_MAIL_LIST_SPORTS) === 0,
        liveAlertSport: (lo & UserPrivileges3.NO_SPORT_LIVE_ALERT) === 0
      };
    });
  },
  getMultiCurrencyPreferences() {
    return slice((state) => {
      const lo = state.flags2?.lo as number;
      return {
        convertBuyIns: (lo & UserFlags2.AUTO_CONVERT_BUY_IN) !== 0,
        convertWinnings: (lo & UserFlags2.AUTO_CONVERT_WINNINGS) !== 0
      };
    });
  },
  getPreferredCurrencyBalance() {
    return slice((state) => state.balances?.[0]?.chips);
  },
  getCurrency() {
    return slice((state) => state.balances?.[0]?.currency);
  },
  getSelfExclusion() {
    return slice((state) => ((state.privileges as number) & UserPrivileges.SELF_EXCLUDED) !== 0);
  },
  isSportsSelfExcluded(this: { getSelfExclusion: Selector<RootState, boolean> }) {
    return createSelector(
      this.getSelfExclusion,
      (rootState: RootState) => select.cms.getBrandId(rootState) === BrandTypes.BET_STARS,
      (isSelfExcluded, isSportsVertical) => isSportsVertical && isSelfExcluded
    );
  },
  getOccupationInfo() {
    return slice((state) => state.occupationInfo);
  },
  getOddsFormat() {
    return slice((state) => state.oddsFormat);
  },
  hasAutoAcceptEnabled() {
    return slice((state) => state.autoAcceptPriceChanges);
  },
  getPhone() {
    return slice((state) => state.phone);
  },
  getMobilePhone() {
    return slice((state) => state.mobilePhone);
  },
  isMobileVerified() {
    return slice((state) => !!((state.flags2?.lo as number) & UserFlags2.MOBILE_VERIFIED));
  },
  isLicenseMobileVerified() {
    return slice((state) => !!((state.flags3?.lo as number) & UserFlags3.LICENSE_MOBILE_VERIFIED));
  },
  getCountry() {
    return slice((state) => state.country);
  },
  getLicense() {
    return slice((state) => state.licenseId as License);
  },
  getTimezone() {
    return slice((state) => state.timezone);
  },
  getEulaMarketingEnabled() {
    return slice((state) => !!state.signEulaOptions && (state.signEulaOptions & 1) === 1);
  },
  getForcedCashoutEnabled() {
    return slice((state) => !!state.forcedCashout);
  },
  getBanPopup() {
    return slice((state) => state.banPopUp || RegulatorBanPopupType.NONE);
  },
  getDepositConfirmLimits() {
    return slice((state) => {
      const { depositConfirmLimits } = state;
      return depositConfirmLimits?.length ? depositConfirmLimits : null;
    });
  },
  getGameConfirmLimits() {
    return slice((state) => {
      const { gameConfirmLimits } = state;
      return gameConfirmLimits?.length ? gameConfirmLimits : null;
    });
  },
  getEmailValidationFlags() {
    return slice((state) => {
      const flags = state.flags as number;
      const hi = state.flags2?.hi as number;
      return {
        verified: !!(flags & EmailFlag.Verified),
        bounced: !!(flags & EmailFlag.Bounced),
        notAuthorized: !!(hi & EmailFlag.NotAuthorized),
        alwaysValidated: !!(hi & EmailFlag.AlwaysValidated)
      };
    });
  },
  getStarsCoinBalance() {
    return slice((state) => Math.floor((state.fppAmount || 0) / 100));
  },
  getDefaultCurrencyForCountry() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => rootState.account.defaultCurrencyForCountry,
      (site, defaultCurrencyForCountry) => {
        if (site === Sites.SPA) {
          return Currency.Euro; // temporal solution while backend doesn't return proper currency
        }
        return defaultCurrencyForCountry;
      }
    );
  },
  getLimitCurrency() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) => {
        switch (site) {
          case Sites.IND:
            return Currency.IndianRupee;
          case Sites.RU:
            return Currency.RussianRuble;
          case Sites.CA_ON:
            return Currency.CanadianDollar;
          case Sites.FRA:
          case Sites.ITA:
          case Sites.POR:
          case Sites.SPA:
            return Currency.Euro;
          default:
            return Currency.USDollar;
        }
      }
    );
  },
  getInfoSelfExcludedMessage() {
    return slice((state) => state.infoSelfExclMsg);
  },
  getSelfExclusionExpirationDate() {
    return slice((state) => state.selfExclusionExpirationDate);
  },
  hasPendingFunds() {
    return slice((state) => !!((state.flags2?.lo as number) & UserFlags2.HAS_RM_NOK_HELD_DEPOSIT));
  },
  isAccountCancelled() {
    return slice((state) => {
      const { privileges, privileges2, flags, licenseId, esCitizenOrResident } = state;
      return (
        ((privileges as number) & UserPrivileges.PLAY) === 0 &&
        ((privileges as number) & UserPrivileges.CHAT) === 0 &&
        ((flags as number) & UserFlags.AUTH_VERIFIED) === 0 &&
        !!(privileges2 && (privileges2 as Flag).hi & UserPrivileges2.SUSPEND_RM) &&
        esCitizenOrResident === false &&
        [License.ES].includes(licenseId as License)
      );
    });
  },
  isWithdrawalCancellationRestricted() {
    return slice(
      (state) => !!((state.privileges3?.lo as number) & UserPrivileges3.NO_CASHOUT_CANCELLATION)
    );
  },
  noWithdraw() {
    return slice((state) => !!((state.privileges3?.hi as number) & UserPrivileges3.NO_WITHDRAWAL));
  },
  accountDetailsRequired() {
    return slice((state) => get(state, 'info.confirmAccountDetails', false));
  },
  isAgeVerified() {
    return slice((state) => !!((state.flags as number) & UserFlags.AGE_VERIFIED));
  },
  isAuthVerified() {
    return slice((state) => !!((state.flags as number) & UserFlags.AUTH_VERIFIED));
  },
  isVulnerablePlayer() {
    return slice((state) => !!((state.flags3?.hi ?? 0) & UserFlags3.EVER_HAD_ELEVATED_RISK_LEVEL));
  },
  isVerifiedForMarketing() {
    return slice((state) => !!((state.flags3?.hi ?? 0) & UserFlags3.VERIFIED_FOR_MARKETING));
  },
  bannedByRegulator() {
    return slice((state) => ((state.flags2?.lo as number) & UserFlags2.BANNED_BY_REGULATOR) !== 0);
  },
  RSAActivated() {
    return slice((state) => !!((state.flags2?.lo as number) & UserFlags2.RSA_ACTIVATED));
  },
  RSATokenEnabled() {
    return slice((state) => !!((state.flags as number) & UserFlags.RSA_REQUIRED));
  },
  getTimestamp() {
    return slice((state) => state.timestamp);
  },
  isEmailValidated(this: { getEmailValidationFlags: Selector<RootState, EmailValidationFlags> }) {
    return createSelector(
      this.getEmailValidationFlags,
      ({ verified, notAuthorized, alwaysValidated }) =>
        !(!verified || notAuthorized || alwaysValidated)
    );
  },
  getDefaultCurrency(this: { getLicense: Selector<RootState, License> }) {
    return createSelector(
      this.getLicense,
      (rootState: RootState) => select.cms.showVegasContent(rootState),
      (rootState: RootState) => select.currency.getRealMoney(rootState),
      (licenseId, showVegasContent, rmCurrency) =>
        showVegasContent || licenseId === License.GR ? Currency.Euro : rmCurrency
    );
  },
  getUserLimits() {
    return slice((state) => state.userLimits);
  },
  isAccountPINEnabled() {
    return slice((state) => !!((state.flags as number) & UserFlags.PSP_REQUIRED));
  },
  isAccountPINGenerated() {
    return slice((state) => !!((state.flags2?.lo as number) & UserFlags2.PSP_GENERATED));
  },
  isOneTimePINEnabled() {
    return slice(
      (state) => !!((state.flags3?.lo as number) & UserFlags3.GENERATE_TEMP_PIN_ON_EACH_LOGIN)
    );
  },
  isMigrationRequired() {
    return slice((state) => !!state.migrationFlag);
  },
  isQuickAccountCreationRequired() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      ({ migrationFlag, migrationTarget }, siteId) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const eRegulatorMigrationFlag_AddCrtAcctOption = 1 << 3;

        return (
          !!migrationFlag &&
          !!migrationTarget &&
          [Sites.USMI, Sites.US_PA].includes(siteId) &&
          [Sites.USMI, Sites.US_PA].includes(migrationTarget) &&
          (migrationFlag & eRegulatorMigrationFlag_AddCrtAcctOption) ===
            eRegulatorMigrationFlag_AddCrtAcctOption
        );
      }
    );
  },
  isEulaRequired() {
    return slice((state) => state.eulaRequired);
  },
  isRealMoneyOK() {
    return slice((state) => !!(state.flags && state.flags & UserFlags.REAL_MONEY_OK));
  },
  isCasinoRealityCheckRequired() {
    return slice((state) => (state.info ? state.info.casinoRealityCheck === null : false));
  },
  isNoSportsPlay() {
    return slice((state) => !!((state.privileges3?.lo as number) & UserPrivileges3.NO_SPORTS_PLAY));
  },
  isSuspendRM() {
    return slice(
      (state) =>
        !!(state.privileges2 && (state.privileges2 as Flag).hi & UserPrivileges2.SUSPEND_RM)
    );
  },
  suspendRmReasonVerification() {
    return slice((state) => state.suspendRMReasons === SuspendRMReasons.DOC_VERIFICATION);
  },
  cannotSendTransfer() {
    return slice(
      (state) =>
        !!(
          state.privileges2 && (state.privileges2 as Flag).lo & UserPrivileges2.CANNOT_SEND_TRANSFER
        )
    );
  },
  loginEmailNotificationEnabled() {
    return slice(
      (state) => !((state.flags2?.hi as number) & UserFlags2.NO_LOGIN_EMAIL_NOTIFICATION)
    );
  },
  isAgeVerificationRequired() {
    return slice((state) => !!state.playerVerification);
  },
  isPasswordWeak() {
    return slice((state) => !!state.passwordWeak);
  },
  showUkaYoungUserDialog() {
    return slice((state) => !!state.showUkaYoungUserDialog);
  },
  showReinstateDialog() {
    return slice((state) => !!state.showReinstateDialog);
  },
  dataIntegrityCheckFailed() {
    return slice((state: AccountState): boolean => !!state.dataIntegrityCheckFailed);
  },
  displayValueProposition() {
    return slice((state: AccountState): boolean => !!state.displayValueProposition);
  },
  amlVerified() {
    return slice(
      (state) => !!((state.flags3?.hi as number) & UserFlags3.ANTI_MONEY_LAUNDRY_VERIFIED)
    );
  },
  amlDepositThresholdReached() {
    return slice(
      (state) => !!((state.flags3?.hi as number) & UserFlags3.AML_DEPOSIT_THRESHOLD_REACHED)
    );
  },
  amlDocsInProgress() {
    return slice((state) => !!((state.flags3?.hi as number) & UserFlags3.AML_DOCS_IN_PROGRESS));
  },
  amlSuspended() {
    return slice((state) => !!((state.flags3?.hi as number) & UserFlags3.AML_SUSPENDED));
  },
  amlShowVerifiedModal() {
    return slice((state) => !!((state.flags3?.hi as number) & UserFlags3.SHOW_AML_VERIFIED_MODAL));
  },
  disableFormUS(this: {
    getSelfExclusion: Selector<RootState, boolean>;
    getLicense: Selector<RootState, License>;
  }) {
    return createSelector(
      this.getSelfExclusion,
      this.getLicense,
      (selfExclusion, licenseId) =>
        [License.USPA, License.USMI, License.USNJ].includes(licenseId) && selfExclusion
    );
  },
  passwordNotRequired() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (siteId) => [Sites.CH, Sites.IT_SISAL_EXT].includes(siteId)
    );
  },
  isEUDE(this: { getCountry: Selector<RootState, Country> }) {
    return createSelector(
      this.getCountry,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (country, site) => site === Sites.EU && country === Country.Germany
    );
  },
  requireDocumentValidation(this: {
    getDateOfBirth: Selector<RootState, Date | undefined>;
    isAuthVerified: Selector<RootState, boolean>;
    isRealMoneyOK: Selector<RootState, boolean>;
    isAgeVerified: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.getDateOfBirth,
      this.isAuthVerified,
      this.isRealMoneyOK,
      this.isAgeVerified,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => rootState.feature?.[FeatureFlag.DocumentsRequestPrompt],
      (
        dateOfBirth,
        isAuthVerified,
        isRealMoneyOK,
        isAgeVerified,
        siteId,
        country,
        documentsRequestPrompt
      ) =>
        siteId === Sites.EU && country === Country.Netherlands
          ? !!dateOfBirth && !isRealMoneyOK
          : documentsRequestPrompt && isAuthVerified && isRealMoneyOK && !isAgeVerified
    );
  },
  addressChangeNotAllowed(this: {
    getSelfExclusion: Selector<RootState, boolean>;
    getLicense: Selector<RootState, License>;
    bannedByRegulator: Selector<RootState, boolean>;
    isSuspendRM: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.getSelfExclusion,
      this.getLicense,
      this.bannedByRegulator,
      this.isSuspendRM,
      (selfExclusion, licenseId, bannedByRegulator, suspendedRM) =>
        (selfExclusion && [License.USNJ, License.USPA, License.USMI].includes(licenseId)) ||
        (licenseId === License.USMI && (bannedByRegulator || suspendedRM))
    );
  },
  isReloader() {
    return slice((state) => !!state.reloader);
  },
  tempAccount() {
    return slice((state) => !!state.tempAccount);
  },
  isSelfExcluded() {
    return slice((state) => !!state.selfExcluded);
  },
  selfExclusionExpired() {
    return slice((state) => !!state.selfExclusionExpired);
  },
  isVerifyWithdrawalMethod() {
    return slice((state) => !!state.verifyWithdrawalMethod);
  },
  getWebClientDialog() {
    return slice((state) => state.webClientDialog);
  },
  getNetDepositLimitGroup() {
    return slice((state) => state.netDepositLimitGroup);
  },
  getAckLifetimeLimit() {
    return slice((state) => state.lifetimeLimitAckLifetimeLimit);
  },
  checkLifetimeLimitAck() {
    return slice((state) => !!state.lifetimeLimitAckAction);
  },
  getLifetimeLimitAckType() {
    return slice((state) => state.lifetimeLimitAckTypeOfPopUp);
  },
  getUserIntId() {
    return slice((state) => state.userIntId);
  },
  isDormantAccount() {
    return slice((state) => !!state.dormantSuspended);
  },
  isEsCitizenOrResident() {
    return slice((state) => !!state.esCitizenOrResident);
  },
  shouldPreventLogin() {
    return slice((state) => !!state.preventLogin);
  },
  getDepositLimitPath() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) =>
        `/account/responsible/restrict-deposit-limit${
          depositLimitExtendedSites.includes(site) ? '-extended' : ''
        }`
    );
  },
  isTemporaryAccount(this: {
    isRealMoneyOK: Selector<RootState, boolean>;
    isAgeVerified: Selector<RootState, boolean>;
    getAccountSummary: Selector<RootState, AccountSummary>;
  }) {
    return createSelector(
      this.isRealMoneyOK,
      this.isAgeVerified,
      this.getAccountSummary,
      (isRealMoneyOK, isAgeVerified, accountSummary) =>
        isRealMoneyOK &&
        !isAgeVerified &&
        !!accountSummary.type &&
        [
          RegulatorPlayerValPopupType.DOC_VERIFICATION_REQUIRED,
          RegulatorPlayerValPopupType.RM_ACTIVITY_RESTRICTED
        ].includes(accountSummary.type)
    );
  },
  getPlayerValPopupType() {
    return slice((state) => state.playerValPopupType);
  },
  isKYCRequired() {
    return slice((state) => !!((state.flags3?.hi as number) & UserFlags3.KYC_REQUIRED));
  },
  pendingVerification() {
    return createSelector(
      this.isKYCRequired,
      this.isAuthVerified,
      this.isRealMoneyOK,
      this.isAgeVerified,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (isKYCRequired, isAuthVerified, isRealMoneyOK, isAgeVerified, site) =>
        site === Sites.CA_ON &&
        !isKYCRequired &&
        (!isAuthVerified || !isRealMoneyOK || !isAgeVerified)
    );
  },
  isRomanianTemporaryAccount(this: {
    getLicense: Selector<RootState, License>;
    isRealMoneyOK: Selector<RootState, boolean>;
    isAgeVerified: Selector<RootState, boolean>;
    isAuthVerified: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.getLicense,
      this.isRealMoneyOK,
      this.isAgeVerified,
      this.isAuthVerified,
      (licenseId, isRealMoneyOK, isAgeVerified, isAuthVerified) =>
        licenseId === License.RO && isRealMoneyOK && (!isAgeVerified || !isAuthVerified)
    );
  },
  showCashierTour() {
    return createSelector(
      (rootState: RootState) => select.session.isAuthenticated(rootState),
      (rootState: RootState) => rootState.feature?.[FeatureFlag.SwcNewCashierNotification],
      (isAuthenticated, SwcNewCashierNotification) => isAuthenticated && SwcNewCashierNotification
    );
  },
  isLimitedAccount(this: {
    getLicense: Selector<RootState, License>;
    isRealMoneyOK: Selector<RootState, boolean>;
    isAgeVerified: Selector<RootState, boolean>;
    isAuthVerified: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.getLicense,
      this.isRealMoneyOK,
      this.isAgeVerified,
      this.isAuthVerified,
      (licenseId, isRealMoneyOK, isAgeVerified, isAuthVerified) => {
        if ([License.RO, License.IT, License.CZ, License.FR].includes(licenseId)) {
          return isRealMoneyOK && (!isAgeVerified || !isAuthVerified);
        } else if (licenseId === License.ES) {
          return isRealMoneyOK && isAuthVerified && !isAgeVerified;
        }

        return false;
      }
    );
  },
  getMigrationTarget() {
    return slice((state) => state.migrationTarget);
  },
  shouldOpenRmf(this: {
    isKYCRequired: Selector<RootState, License>;
    isAgeVerificationRequired: Selector<RootState, License>;
    dataIntegrityCheckFailed: Selector<RootState, License>;
  }) {
    return createSelector(
      this.isKYCRequired,
      this.isAgeVerificationRequired,
      this.dataIntegrityCheckFailed,
      (rootState: RootState) => rootState.feature[FeatureFlag.DisableNewUkAgeVerificationFlow],
      (rootState: RootState) => rootState.feature[FeatureFlag.WrmfAcAutotrigger],
      (rootState: RootState, loginSource: LoginSource | undefined) => loginSource,
      (
        isKYCRequired,
        isAgeVerificationRequired,
        dataIntegrityCheckFailed,
        disableNewUkAgeVerificationFlow,
        wrmfAcAutotrigger,
        loginSource
      ) =>
        !isKYCRequired &&
        ((!disableNewUkAgeVerificationFlow && isAgeVerificationRequired) ||
          dataIntegrityCheckFailed ||
          (wrmfAcAutotrigger && loginSource === LoginSource.Registration))
    );
  },
  isUSLicense(this: { getLicense: Selector<RootState, License> }) {
    return createSelector(this.getLicense, (licenseId) =>
      [License.USMI, License.USNJ, License.USPA, License.USCO].includes(licenseId)
    );
  },
  monthlyNetDepositLimit() {
    return slice((state) => state.monthlyNetDepositLimit);
  },
  netDepositLimitCurrency() {
    return slice((state) => state.netDepositLimitCurrency);
  },
  isSWCWithdraw() {
    return createSelector(
      (rootState: RootState) => rootState.feature?.[FeatureFlag.SwcWithdrawal],
      (isSWCWithdrawEnabled) => !!isSWCWithdrawEnabled
    );
  },
  isSWCTransactionHistory() {
    return createSelector(
      (rootState: RootState) => rootState.feature?.[FeatureFlag.SwcCashier],
      (isSWCTransactionHistoryEnabled) => !!isSWCTransactionHistoryEnabled
    );
  },
  isSWCManageCurrencies() {
    return createSelector(
      (rootState: RootState) => rootState.feature?.[FeatureFlag.SwcManageCurrencies],
      (isSWCManageCurrenciesEnabled) => !!isSWCManageCurrenciesEnabled
    );
  },
  isSWCP2pTransfer() {
    return createSelector(
      (rootState: RootState) => rootState.feature?.[FeatureFlag.SwcP2pTransfer],
      (isSWCP2pTransferEnabled) => !!isSWCP2pTransferEnabled
    );
  },
  isSWCCashierHome() {
    return createSelector(
      (rootState: RootState) => rootState.feature?.[FeatureFlag.SwcCashier],
      (isSWCCashierEnabled) => !!isSWCCashierEnabled
    );
  },
  isSWCDevelop(this: { isReloader: Selector<RootState, boolean> }) {
    return createSelector(
      this.isReloader,
      (rootState: RootState, rmfPassed: boolean) => {
        const { feature } = rootState;
        return {
          hspSwcDeposit: feature?.[FeatureFlag.SwcDeposit],
          hspSwcSignupDeposit: feature?.[FeatureFlag.SwcSignupDeposit],
          hspSwcDepositReloader: feature?.[FeatureFlag.SwcDepositReloader],
          rmfPassed
        };
      },
      (isReloader, { rmfPassed, hspSwcDeposit, hspSwcSignupDeposit, hspSwcDepositReloader }) =>
        !!(rmfPassed && hspSwcSignupDeposit) ||
        (!!hspSwcDeposit && !isReloader) ||
        !!(hspSwcDeposit && isReloader && hspSwcDepositReloader)
    );
  },
  isSWCDeposit(this: { isReloader: Selector<RootState, boolean> }) {
    return createSelector(
      this.isReloader,
      (rootState: RootState, rmfPassed: boolean) => {
        const { feature } = rootState;
        return {
          hspSwcDeposit: feature?.[FeatureFlag.SwcDeposit],
          hspSwcSignupDeposit: feature?.[FeatureFlag.SwcSignupDeposit],
          hspSwcDepositReloader: feature?.[FeatureFlag.SwcDepositReloader],
          rmfPassed
        };
      },
      (isReloader, { rmfPassed, hspSwcDeposit, hspSwcSignupDeposit, hspSwcDepositReloader }) =>
        !!(rmfPassed && hspSwcSignupDeposit) ||
        (!!hspSwcDeposit && !isReloader) ||
        !!(hspSwcDeposit && isReloader && hspSwcDepositReloader)
    );
  },
  hideBonusCode() {
    return slice(
      (state) => !!((state.flags3?.hi as number) & UserFlags3.EVER_HAD_ELEVATED_RISK_LEVEL)
    );
  },
  getLugasPlayerStatusLastAckDate() {
    return slice((state) => {
      if (state.lugasPlayerStatus?.lastAcknowledgementDate) {
        return new Date(state.lugasPlayerStatus?.lastAcknowledgementDate);
      }
      return null;
    });
  },
  allowHistoryAuditReport(this: { getLicense: Selector<RootState, License> }) {
    return createSelector(this.getLicense, (license) =>
      [License.USNJ, License.USMI, License.USPA].includes(license)
    );
  },
  allowAccountReopeningRequest() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) =>
        ![Sites.SPA, Sites.SWE, Sites.ROM, Sites.BEL, Sites.CZE, Sites.EST, Sites.BUL].includes(
          site
        )
    );
  },
  isDeAnnualAttestation() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      ({ accValEventType }, siteId) =>
        Sites.DE === siteId && accValEventType === RegulatorAccValEventType.WAIT_ANN_ATT
    );
  },
  isDeAttestationSuspended() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      ({ accValEventType }, siteId) =>
        Sites.DE === siteId && accValEventType === RegulatorAccValEventType.SUSPENDED_BY_ANN_ATT
    );
  },
  isRetryScreenPassed() {
    return slice((state) => !!state.isRetryScreenPassed);
  },
  hasUkSpendBudgetLimitEnabled() {
    return slice(
      (state) => !!((state.flags3?.hi as number) & UserFlags3.PLAYED_RM_LIVE_CASINO_GAME)
    );
  }
});

export default selectors;
